import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { ExternalLink } from "lucide-react";

import { logger } from "../lib/log";
/**
 * AdStrip — renders affiliate placements for a given slot with impression &
 * click tracking (once per session per ad).
 */

const sessionKey = (id) => `atelier_ad_seen_${id}`;

const AdStrip = ({ slot, heading = "In collaboration", subheading = "Sponsored — hand-picked partners", limit = 6, layout = "row" }) => {
  const [ads, setAds] = useState([]);
  const observed = useRef(new Set());

  useEffect(() => {
    api.get(`/ads?slot=${slot}&limit=${limit}`).then((r) => setAds(r.data))
      .catch((e) => logger.warn("[ads fetch]", slot, e?.message));
  }, [slot, limit]);

  useEffect(() => {
    if (!ads.length) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const id = entry.target.dataset.adId;
        if (!id || observed.current.has(id)) return;
        observed.current.add(id);
        // Dedupe impression tracking within a browser session — the value is
        // just "1", never a token or PII. Cleared on tab close.
        if (sessionStorage.getItem(sessionKey(id))) return;
        sessionStorage.setItem(sessionKey(id), "1");
        api.post(`/ads/${id}/impression`).catch((e) => logger.warn("[ad impression]", id, e?.message));
      });
    }, { threshold: 0.4 });
    document.querySelectorAll(`[data-ad-strip="${slot}"] [data-ad-id]`).forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [ads, slot]);

  const handleClick = (id) => {
    // fire and forget; use sendBeacon fallback for reliability on nav
    try {
      if (navigator.sendBeacon) {
        // sendBeacon requires content, but our endpoint reads no body
        navigator.sendBeacon(`${api.defaults.baseURL}/ads/${id}/click`, new Blob([""], { type: "text/plain" }));
      } else {
        api.post(`/ads/${id}/click`).catch((e)=>logger.warn("[ad click]", e?.message));
      }
    } catch (e) { logger.warn("[ad click sync]", e?.message); }
  };

  if (!ads.length) return null;

  return (
    <section data-ad-strip={slot} className="my-12 border-t border-black/10 pt-8">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="font-mono-eyebrow text-[color:var(--accent)]">{subheading}</div>
          <h3 className="font-serif-display text-3xl leading-tight mt-1">{heading}</h3>
        </div>
        <span className="font-mono-eyebrow text-black/40">Affiliate</span>
      </div>
      <div className={layout === "grid" ? "grid grid-cols-2 md:grid-cols-3 gap-4" : "grid grid-cols-2 md:grid-cols-4 gap-4"}>
        {ads.map((a) => (
          <a key={a.id} href={a.target_url} target="_blank" rel="noopener noreferrer sponsored"
             data-testid={`ad-card-${a.id}`}
             data-ad-id={a.id}
             onClick={() => handleClick(a.id)}
             onAuxClick={() => handleClick(a.id)}
             className="group relative bg-white border border-black/10 block hover:border-black transition-colors">
            <div className="aspect-[3/4] overflow-hidden bg-neutral-100">
              <img src={a.image_url} alt={a.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"/>
            </div>
            <span className="absolute top-3 left-3 bg-black/70 text-white text-[9px] uppercase tracking-widest px-2 py-1 backdrop-blur">Sponsored</span>
            <div className="p-4">
              <div className="font-mono-eyebrow text-black/40">{a.brand}</div>
              <div className="font-serif-display text-lg leading-tight line-clamp-2 mt-1">{a.title}</div>
              <div className="flex justify-between items-center mt-3">
                <span className="font-mono-eyebrow capitalize text-black/60">{a.category || "shop"}</span>
                <ExternalLink className="w-3.5 h-3.5 text-black/40 group-hover:text-black transition-colors"/>
              </div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
};

export default AdStrip;
